import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import NavBar from './NavBar';
import TopBar from './TopBar';
import useAuth from 'src/hooks/useAuth';
import { useHistory } from 'react-router-dom';
import { channel } from 'src/service/pusher';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import { ExternalLink as ExternalLinkIcon } from 'react-feather';
import useDPXConversations from 'src/hooks/useDPXConversations';
import { WarboxNotifications } from 'src/components/Alerts/components';
import ImpersonationBanner from 'src/components/ImpersonationBanner';
import { AssistantWidget } from 'src/modules/assistant/components';
import { useAssistantContext } from 'src/modules/assistant/hooks/useAssistantContext';
import { WIDGET_RESERVED_SPACE_PX } from 'src/modules/assistant/constants/widget';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.dark,
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        width: '100%',
    },
    wrapper: {
        display: 'flex',
        flex: '1 1 auto',
        overflow: 'hidden',
        paddingTop: 80,
        [theme.breakpoints.up('lg')]: {
            paddingLeft: 275,
        },
    },
    contentContainer: {
        display: 'flex',
        flex: '1 1 auto',
        overflow: 'hidden',
    },
    content: {
        flex: '1 1 auto',
        height: '100%',
        overflow: 'auto',
    },
    // Lets the last controls of the page scroll above the assistant widget.
    contentWithAssistant: {
        paddingBottom: WIDGET_RESERVED_SPACE_PX,
    },
}));

const DashboardLayout = ({ children }) => {
    const classes = useStyles();
    const [isMobileNavOpen, setMobileNavOpen] = useState(false);
    const { loadUser, user } = useAuth();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const { t } = useTranslation();
    const { getConversation, clearConversation } = useDPXConversations();
    const { assistantVisible } = useAssistantContext();

    useEffect(() => {
        if (!user || JSON.stringify(user) === '{}') loadUser();
    }, []);

    useEffect(() => {
        if (!user?._id) return;
        const pendingPath = sessionStorage.getItem('auth.pendingPath');
        if (pendingPath) {
            sessionStorage.removeItem('auth.pendingPath');
            history.push(pendingPath);
        }
    }, [user, history]);

    const actionIcomingMsg = (data) => {
        <IconButton
            onClick={() => {
                clearConversation();

                if (data.conversation) getConversation(data.conversation);
                history.push('/conversations');
            }}
        >
            <ExternalLinkIcon />
        </IconButton>;
    };

    useEffect(() => {
        if (user && user._id) {
            channel.unbind(`incoming_msg_by_agent_${user._id}`);
            channel.bind(`incoming_msg_by_agent_${user._id}`, (data) => {
                enqueueSnackbar(
                    `${t('SnackBar.IncomingMessage')} ${CapitalizeNames(
                        data.channel
                    )}`,
                    {
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                        variant: 'info',
                        action: actionIcomingMsg(data),
                    }
                );
            });
        }
    }, []);

    return (
        <Fragment>
            <WarboxNotifications />
            <AssistantWidget />
            <div className={classes.root}>
                <TopBar onMobileNavOpen={() => setMobileNavOpen(true)} />
                <NavBar
                    onMobileClose={() => setMobileNavOpen(false)}
                    openMobile={isMobileNavOpen}
                />
                <div className={classes.wrapper}>
                    <div className={classes.contentContainer}>
                        <div
                            className={`${classes.content} ${
                                assistantVisible
                                    ? classes.contentWithAssistant
                                    : ''
                            }`}
                        >
                            <ImpersonationBanner />
                            {children && children}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

DashboardLayout.propTypes = {
    children: PropTypes.node,
};

export default DashboardLayout;
